import type { ProgramPagePdfData } from "../programPages/types";
import type { MetricsStreamingFormat } from "../miContenidoExportData";
import {
  buildDelta,
  fmtCompactPlatform,
  fmtLiveMetric,
  type PeriodSummaryPlatform,
  type PeriodSummaryPlatformRow,
} from "./buildPeriodSummaryData";
import { metricsStreamingLabels } from "./metricsStreamingLabels";

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function metricRow(
  label: string,
  current: number,
  previous: number | undefined,
  options?: { live?: boolean; zeroAsDash?: boolean },
): PeriodSummaryPlatformRow {
  const value =
    options?.live || options?.zeroAsDash
      ? options?.live
        ? fmtLiveMetric(current)
        : current > 0
          ? fmtCompactPlatform(current)
          : "—"
      : fmtCompactPlatform(current);

  return {
    label,
    value,
    delta:
      previous != null && previous !== undefined
        ? buildDelta(current, previous)
        : undefined,
  };
}

function surfaceRow(
  page: ProgramPagePdfData,
  surface: string,
  previous = false,
): ProgramPagePdfData["metricRows"][number] | undefined {
  const rows = previous ? page.previousMetricRows : page.metricRows;
  return rows?.find((r) => r.surface === surface);
}

const ALWAYS_SHOW_PLATFORM_KEYS = new Set<PeriodSummaryPlatform["key"]>([
  "youtube_shorts",
  "instagram",
  "tiktok",
]);

function contentPlatformCard(
  key: PeriodSummaryPlatform["key"],
  label: string,
  formats: string,
  hasPrevious: boolean,
  metricRowData: ProgramPagePdfData["metricRows"][number] | undefined,
  previousMetricRowData: ProgramPagePdfData["metricRows"][number] | undefined,
): PeriodSummaryPlatform {
  const views = num(metricRowData?.totalViews);
  const interactions = num(metricRowData?.totalInteractions);
  const content = num(metricRowData?.contentCount);
  const prevViews = num(previousMetricRowData?.totalViews);
  const prevInteractions = num(previousMetricRowData?.totalInteractions);
  const prevContent = num(previousMetricRowData?.contentCount);

  return {
    key,
    label,
    formats,
    rows: [
      metricRow("Views", views, hasPrevious ? prevViews : undefined),
      metricRow(
        "Interac.",
        interactions,
        hasPrevious ? prevInteractions : undefined,
      ),
      metricRow("Contenidos", content, hasPrevious ? prevContent : undefined),
    ],
    hasData:
      ALWAYS_SHOW_PLATFORM_KEYS.has(key) ||
      views > 0 ||
      interactions > 0 ||
      content > 0,
  };
}

export function buildProgramPlatformSummary(
  page: ProgramPagePdfData,
  streamingFormat: MetricsStreamingFormat = "live",
): PeriodSummaryPlatform[] {
  const liveCardFormat =
    page.livestream?.streamingFormat ?? streamingFormat;
  const liveLabels = metricsStreamingLabels(liveCardFormat);
  const liveFormats = liveLabels.streamsPlural;
  const hasPrevious = page.previousMetricRows !== undefined;

  const platforms: PeriodSummaryPlatform[] = [
    contentPlatformCard(
      "youtube_videos",
      "YouTube",
      "Videos",
      hasPrevious,
      surfaceRow(page, "YouTube Videos"),
      surfaceRow(page, "YouTube Videos", true),
    ),
    {
      key: "podscope",
      label: "YouTube",
      formats: liveFormats,
      rows: page.livestream
        ? [
            metricRow(
              liveLabels.promAvg,
              page.livestream.avgViewers,
              hasPrevious ? num(page.previousLivestream?.avgViewers) : undefined,
              { live: true },
            ),
            metricRow(
              liveLabels.peak,
              page.livestream.peakViewers,
              hasPrevious ? num(page.previousLivestream?.peakViewers) : undefined,
              { live: true },
            ),
            metricRow(
              liveFormats,
              page.livestream.streamCount,
              hasPrevious ? num(page.previousLivestream?.streamCount) : undefined,
              { zeroAsDash: true },
            ),
          ]
        : [],
      hasData: !!page.livestream &&
        (page.livestream.streamCount > 0 ||
          page.livestream.avgViewers > 0 ||
          page.livestream.peakViewers > 0),
    },
    contentPlatformCard(
      "instagram",
      "Instagram",
      "Reels",
      hasPrevious,
      surfaceRow(page, "Instagram"),
      surfaceRow(page, "Instagram", true),
    ),
    contentPlatformCard(
      "facebook",
      "Facebook",
      "Posts",
      hasPrevious,
      surfaceRow(page, "Facebook"),
      surfaceRow(page, "Facebook", true),
    ),
    contentPlatformCard(
      "tiktok",
      "TikTok",
      "Videos",
      hasPrevious,
      surfaceRow(page, "TikTok"),
      surfaceRow(page, "TikTok", true),
    ),
    contentPlatformCard(
      "youtube_shorts",
      "YouTube",
      "Shorts",
      hasPrevious,
      surfaceRow(page, "YouTube Shorts"),
      surfaceRow(page, "YouTube Shorts", true),
    ),
  ];

  return platforms;
}

export function programPlatformSummaryHasData(
  platforms: PeriodSummaryPlatform[],
): boolean {
  return platforms.some((p) => p.hasData);
}
